import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";

import type { AiTrackerReportsBrandOverviewDailyBrandVisibilityGraphData } from "@similarweb/pro-api-client";
import { CardGroup, GraphMetricCard } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import type { IChangeValue } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { percentageSignFilter } from "app/filters/ngFilters";
import { CardBenchmarkRow } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/CardBenchmarkRow";
import { useBrandOverviewData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/hooks/useBrandOverviewData";
import {
    getBaseWidgetRequestParams,
    getTopicsFilterParam,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

export const BrandOverviewComponent = ({
    campaign,
    campaignConfig,
    reportStatus,
    reportDuration,
}: ILLMReportWidget) => {
    const translate = useTranslation();
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;

    const {
        data,
        isLoading: isLoadingData,
        isError,
    } = useBrandOverviewData(
        {
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            topics: getTopicsFilterParam(queryTopic),
        },
        { enabled: canLoadData },
    );

    const hasData = !!data && !isError && reportStatus !== ReportStatus.Error;
    const shouldShowLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;

    const getGraphDataFromArray = (
        dailyDataArray: AiTrackerReportsBrandOverviewDailyBrandVisibilityGraphData[],
        name: string,
    ) => {
        if (!dailyDataArray || !name) {
            return null;
        }

        return [
            {
                name,
                id: name,
                visible: true,
                data: dailyDataArray.map((item) => [new Date(item.date ?? "").getTime(), item.value]),
            },
        ];
    };

    const visibilityGraphData = useMemo(
        () =>
            data?.brandVisibility?.total?.dailyData?.length
                ? getGraphDataFromArray(data.brandVisibility.total.dailyData, "brandVisibility")
                : null,
        [data],
    );

    const mentionShareGraphData = useMemo(
        () =>
            data?.brandMentionShare?.total?.dailyData?.length
                ? getGraphDataFromArray(data.brandMentionShare.total.dailyData, "brandMentionShare")
                : null,
        [data],
    );

    const graphPropsWithPercentage = {
        chartType: chartTypes.AREA,
        granularity: GranularityNames.DAILY,
        tooltipValueFormatter: (value: number) => percentageSignFilter()(value, 2),
    };

    const getPopChangeValueProps = (change: number | null | undefined): IChangeValue | undefined => {
        if (change == null) {
            return undefined;
        }
        return {
            value: Math.abs(change).toFixed(2),
            isDecrease: change < 0,
            descriptionText: translate("llm.brand_overview.pop", {}, "PoP"),
        };
    };

    const visibilitySecondaryValueKey = "llm.brand_overview.visibility.text";
    const mentionShareSecondaryValueKey = "llm.brand_overview.mention_share.text";

    const coreCompVisibilityRank = data?.competitive?.visibilityRank;
    const outOfCompetitorsMentionShare = data?.competitive?.outOfCompetitorsMentionShare;

    const visibilitySecondaryValue = hasData ? (
        <Stack gap="2px">
            <Text fontVariant="queryBar1">
                {translate(visibilitySecondaryValueKey, {
                    brand: data?.brandVisibility?.myBrands ? Object.keys(data.brandVisibility.myBrands)[0] : undefined,
                    mentions: (data?.brandVisibility?.total?.brandMentions ?? 0).toLocaleString(),
                    total: (data?.brandVisibility?.total?.totalResponses ?? 0).toLocaleString(),
                })}
            </Text>
            {coreCompVisibilityRank != null && (
                <CardBenchmarkRow
                    label={translate("llm.brand_overview.visibility.rank")}
                    formattedValue={`#${coreCompVisibilityRank}`}
                    valueColor={colorsPalettes.blue[500]}
                    dataAutomation="brand-overview-visibility-core-rank"
                />
            )}
        </Stack>
    ) : null;

    const mentionShareSecondaryValue = hasData ? (
        <Stack gap="2px">
            <Text fontVariant="queryBar1">
                {translate(mentionShareSecondaryValueKey, {
                    brand: data?.brandMentionShare?.myBrands
                        ? Object.keys(data.brandMentionShare.myBrands)[0]
                        : undefined,
                    mentions: (data?.brandMentionShare?.total?.brandMentions ?? 0).toLocaleString(),
                    total: (data?.brandMentionShare?.total?.totalAllBrandMentions ?? 0).toLocaleString(),
                })}
            </Text>
            {outOfCompetitorsMentionShare != null && (
                <CardBenchmarkRow
                    label={translate("llm.brand_overview.mention_share.out_of_competitors")}
                    formattedValue={percentageSignFilter()(outOfCompetitorsMentionShare, 2) as string}
                    dataAutomation="brand-overview-mention-share-out-of-core"
                />
            )}
        </Stack>
    ) : null;

    return (
        <CardGroup
            data-automation="brand-overview-widget"
            $height={coreCompVisibilityRank != null || outOfCompetitorsMentionShare != null ? "138px" : "120px"}
        >
            <GraphMetricCard
                data-automation="brand-overview-visibility-card"
                title={translate("llm.brand_overview.visibility.title")}
                tooltip={
                    <Stack>
                        <Text
                            fontVariant="tooltip"
                            maxWidth="250px"
                        >
                            {translate("llm.brand_overview.visibility.tooltip")}
                        </Text>
                    </Stack>
                }
                secondaryValue={visibilitySecondaryValue}
                value={hasData ? percentageSignFilter()(data?.brandVisibility?.total?.brandVisibilityShare, 2) : null}
                changeValueProps={
                    hasData
                        ? getPopChangeValueProps(data?.brandVisibility?.total?.brandVisibilityShareChange)
                        : undefined
                }
                isLoading={shouldShowLoading}
                graphProps={{
                    ...graphPropsWithPercentage,
                    data: visibilityGraphData,
                }}
                onCtaClick={noop}
            />
            <GraphMetricCard
                data-automation="brand-overview-mention-share-card"
                title={translate("llm.brand_overview.mention_share.title")}
                tooltip={
                    <Stack>
                        <Text
                            fontVariant="tooltip"
                            maxWidth="250px"
                        >
                            {translate("llm.brand_overview.mention_share.tooltip")}
                        </Text>
                    </Stack>
                }
                secondaryValue={mentionShareSecondaryValue}
                value={hasData ? percentageSignFilter()(data?.brandMentionShare?.total?.brandMentionShare, 2) : null}
                changeValueProps={
                    hasData
                        ? getPopChangeValueProps(data?.brandMentionShare?.total?.brandMentionShareChange)
                        : undefined
                }
                isLoading={shouldShowLoading}
                graphProps={{
                    ...graphPropsWithPercentage,
                    data: mentionShareGraphData,
                }}
                onCtaClick={noop}
            />
        </CardGroup>
    );
};
